import { NgModule, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterModule, RouterStateSnapshot, Routes } from '@angular/router';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { LoginComponent } from './pages/login/login.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { SignupComponent } from './pages/signup/signup.component';
import { LobbyComponent } from './pages/lobby/lobby.component';
import { EstimatesComponent } from './pages/estimates/estimates.component';
import { EstimateComponent } from "./pages/estimate/estimate.component";
import { InvoicesComponent } from './pages/invoices/invoices.component';
import { canOpenItemsPage, ItemsComponent } from "./pages/items/items.component";
import { ScheduleComponent } from './pages/schedule/schedule.component';
import { CreateBusinessComponent } from './pages/create-business/create-business.component';
import { InvoiceComponent } from "./pages/invoice/invoice.component";
import { PaymentComponent } from "./pages/payment/payment.component";
import { ReportsComponent } from "./pages/reports/reports.component";
import { ReportsPaymentsComponent } from "./pages/reports-payments/reports-payments.component";
import { ReportsCommissionsComponent } from "./pages/reports-commissions/reports-commissions.component";
import { ReportsTechPartsComponent } from "./pages/reports-tech-parts/reports-tech-parts.component";
import { ReportsCreditCardComponent } from "./pages/reports-credit-card/reports-credit-card.component";
import { ReportsOverlookComponent } from "./pages/reports-overlook/reports-overlook.component";
import { ReportsCompareComponent } from "./pages/reports-compare/reports-compare.component";
import { CustomerListComponent } from "./pages/customer-list/customer-list.component";
import { canViewItemGuard } from './services/navigation.service';
import { AssignedComponent } from './pages/assigned/assigned.component';
import { AssignmentComponent } from './pages/assignment/assignment.component';
import { canOpenAssignmentsPage } from './services/assignment.service';
import { canOpenAssignedPage } from './services/assigned.service';
import { SuspendedBusinessComponent } from './pages/suspended-business/suspended-business.component';
import { BusinessService } from './services/business.service';
import { AuthService } from './services/auth.service';
import { take } from 'rxjs/operators';
import { ResetComponent } from "./pages/reset/reset.component";
import { GoogleAuthResultComponent } from "./pages/google-auth-result/google-auth-result.component";
import { GoogleAuthComponent } from "./pages/google-auth/google-auth.component";
import { JoinComponent } from "./pages/join/join.component";
import { LeadsComponent } from "./pages/leads/leads.component";
import { AssessmentsComponent } from "./pages/assessments/assessments.component";
import { JobsComponent } from "./pages/jobs/jobs.component";
import { LoadingComponent } from "./components/loading/loading.component";
import { canOpenSingleUserPage, ViewAsService } from "./services/view-as.service";
import { AvailabilityComponent } from "./pages/availability/availability.component";
import { TechPartsReportItemsComponent } from "./components/tech-parts-report-items/tech-parts-report-items.component";
import { PaymentsReportItemsComponent } from "./components/payments-report-items/payments-report-items.component";
import {
  CommissionsReportItemsComponent
} from "./components/commissions-report-items/commissions-report-items.component";
import { AllScheduleComponent } from "./pages/all-schedule/all-schedule.component";
import { EoIListComponent } from './pages/eoi-list/eoi-list.component';
import {
  canViewBusiness,
  canViewNuveiApplication,
  canViewSuspendedPage
} from "./guards/business.guard";
import { AfterLoginComponent } from "./components/after-login/after-login.component";
import { NuveiApplicationComponent } from "./components/nuvei-application/nuvei-application.component";
import { FileTestComponent } from "./components/file-test/file-test.component";
import {
  NuveiApplicationGuarantorComponent
} from "./components/nuvei-application/nuvei-application-guarantor/nuvei-application-guarantor.component";
import { paymentApplicationIdGuard } from "./guards/payment-application.guard";
import { AssessmentComponent } from './pages/event-info/assessment-or-job/assessment.component';
import { JobComponent } from './pages/event-info/assessment-or-job/job.component';
import { ReportsTipsComponent } from './pages/reports-tips/reports-tips.component';
import { canOpenCommissionsReport } from './helpers/reports/commissions';
import { LeadImportComponent } from './pages/lead-import/lead-import.component';

export const singleUserPages = ['settings', 'items', 'assigned', 'assignment'];

const redirectToLobby = async (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const user = await authService.user$.pipe(take(1)).toPromise();

  const joinContext = localStorage.getItem('join-context');
  if (joinContext) {
    console.log('most')
    const joinData = JSON.parse(joinContext);
    return router.createUrlTree(['/join', `${joinData.businessId}-${joinData.userId}`], {
      queryParams: { email: joinData.email }
    });
  }

  if(user) {
    return router.createUrlTree(['/lobby']);
  }
  return true;

};
const redirectToJoin = async (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const user = await authService.user$.pipe(take(1)).toPromise();

  const joinContext = localStorage.getItem('join-context');
  if (joinContext) {
    console.log('most')
    const joinData = JSON.parse(joinContext);
    return router.createUrlTree(['/join', `${joinData.businessId}-${joinData.userId}`], {
      queryParams: { email: joinData.email }
    });
  }

  if(user && route.url.length > 1) {
    const id = route.url[1].path;
    return router.createUrlTree(['/join', id]);
  }
  return true;
};

const joinWithoutId = async (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const businessService = inject(BusinessService);
  const router = inject(Router);
  const user = await authService.user$.pipe(take(1)).toPromise();

  const id = businessService.getInvitationFromStorage();

  if(id) {
    businessService.removeInvitationFromStorage();
    return router.createUrlTree(['/join', id]);
  }
  return router.createUrlTree(['/lobby']);

};

const redirectUnauthorized = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const viewAsService = inject(ViewAsService);
  const router = inject(Router);
  const businessService = inject(BusinessService);
  const user = await authService.user$.pipe(take(1)).toPromise();
  if(!user) {
    viewAsService.clearSavedState();
    return router.createUrlTree(['/login']);
  }
  const availableBusiness = await businessService.availableBusinesses$.pipe(take(1)).toPromise();
  if(availableBusiness.length === 0) {
    return router.createUrlTree(['/create']);
  }
  return true;
};

const routes: Routes = [
  { path: '', redirectTo: 'lobby', pathMatch: 'full' },
  { path: 'file-test', component: FileTestComponent },
  { path: 'signature/:businessId', component: NuveiApplicationGuarantorComponent },

  { path: 'login', component: LoginComponent, canActivate: [redirectToLobby] },
  { path: 'signup', component: SignupComponent, canActivate: [redirectToJoin] },
  { path: 'signup/:id', component: SignupComponent, canActivate: [redirectToJoin] },
  { path: 'join/:id', component: JoinComponent },
  { path: 'join', component: JoinComponent, canActivate: [joinWithoutId] },
  { path: 'al', component: AfterLoginComponent },
  { path: 'reset-password', component: ForgotPasswordComponent, canActivate: [redirectToLobby] },
  { path: 'change-password', component: ResetComponent, canActivate: [redirectUnauthorized] },

  { path: 'gauth', component: GoogleAuthComponent },
  { path: 'gauthres', component: GoogleAuthResultComponent },

  {path: 'settings', component: ProfileComponent, canActivate: [redirectUnauthorized, canViewBusiness, canOpenSingleUserPage]},
  {path: 'payment-application', component: NuveiApplicationComponent, canActivate: [redirectUnauthorized, canViewNuveiApplication]},
  {path: 'payment-application/:businessId', component: NuveiApplicationComponent, canActivate: [redirectUnauthorized, canViewNuveiApplication, paymentApplicationIdGuard]},
  {
    path: 'reports', component: ReportsComponent, canActivate: [redirectUnauthorized, canViewBusiness], children: [
      {path: '', redirectTo: 'overview', pathMatch: 'full'},
      {
        path: 'transactions', component: ReportsPaymentsComponent, children: [
          {path: '', redirectTo: 'all', pathMatch: 'full'},
          {path: 'all', component: PaymentsReportItemsComponent},
          {path: 'paid', component: PaymentsReportItemsComponent},
          {path: 'pending', component: PaymentsReportItemsComponent},
        ]
      },
      {
        path: 'commissions', component: ReportsCommissionsComponent, canActivate: [canOpenCommissionsReport], children: [
          {path: '', redirectTo: 'pending', pathMatch: 'full'},
          {path: 'pending', component: CommissionsReportItemsComponent},
          {path: 'paid', component: CommissionsReportItemsComponent}
        ]
      },
      {path: 'tech-parts', component: ReportsTechPartsComponent, children: [
          {path: '', redirectTo: 'pending', pathMatch: 'full'},
          {path: 'pending', component: TechPartsReportItemsComponent},
          {path: 'paid', component: TechPartsReportItemsComponent}
        ]
      },
      {path: 'tips', component: ReportsTipsComponent, children: [
          {path: '', redirectTo: 'pending', pathMatch: 'full'},
          {path: 'pending', component: ReportsTipsComponent},
          {path: 'paid', component: ReportsTipsComponent}
        ]
      },
      {path: 'credit-card', component: ReportsCreditCardComponent, children: [
          {path: '', redirectTo: 'settled', pathMatch: 'full'},
          {path: 'settled', component: ReportsCreditCardComponent},
          {path: 'pending', component: ReportsCreditCardComponent},
          {path: 'pre-chargebacks', component: ReportsCreditCardComponent},
          {path: 'chargebacks', component: ReportsCreditCardComponent},
          {path: 'refunds', component: ReportsCreditCardComponent},
          {path: 'surcharge', component: ReportsCreditCardComponent},
        ]
      },
      {path: 'overview', component: ReportsOverlookComponent},
      {path: 'compare', component: ReportsCompareComponent},
    ]
  },

  { path: 'create', component: CreateBusinessComponent },
  { path: 'customer-list', component: CustomerListComponent, canActivate: [redirectUnauthorized, canViewBusiness] },

  { path: 'lead-import', component: LeadImportComponent, canActivate: [redirectUnauthorized, canViewBusiness] },
  {
    path: 'lobby', component: LobbyComponent, canActivate: [redirectUnauthorized, canViewBusiness], children: [
      { path: '', redirectTo: 'schedule', pathMatch: 'full' },
      { path: 'assigned', component: AssignedComponent, canActivate: [canOpenAssignedPage] },
      { path: 'assignment', component: AssignmentComponent, canActivate: [canOpenAssignmentsPage] },
      {
        path: 'schedule', component: ScheduleComponent, children: [
          { path: '', redirectTo: 'all', pathMatch: 'full' },
          { path: 'all', component: AllScheduleComponent },
          { path: 'leads', component: LeadsComponent },
          { path: 'assessments', component: AssessmentsComponent },
          { path: 'jobs', component: JobsComponent },
          { path: 'unavailable', component: AvailabilityComponent },
        ]
      },
      {
        path: 'estimates', component: EstimatesComponent, children: [
          { path: '', redirectTo: 'pending', pathMatch: 'full' },
          { path: 'pending', component: EoIListComponent, data: { type: 'Estimate', tab: 'Pending' } },
          { path: 'won', component: EoIListComponent, data: { type: 'Estimate', tab: 'Won' } },
          { path: 'declined', component: EoIListComponent, data: { type: 'Estimate', tab: 'Declined' } },
          { path: 'canceled', component: EoIListComponent, data: { type: 'Estimate', tab: 'Canceled' } },
          { path: 'drafts', component: EoIListComponent, data: { type: 'Estimate', tab: 'Drafts' } },
        ]
      },
      {
        path: 'invoices', component: InvoicesComponent, children: [
          { path: '', redirectTo: 'pending', pathMatch: 'full' },
          { path: 'pending', component: EoIListComponent, data: { type: 'Invoice', tab: 'Pending' } },
          { path: 'paid', component: EoIListComponent, data: { type: 'Invoice', tab: 'Paid' } },
          { path: 'canceled', component: EoIListComponent, data: { type: 'Invoice', tab: 'Canceled' } },
          { path: 'drafts', component: EoIListComponent, data: { type: 'Invoice', tab: 'Drafts' } }
        ]
      },
    ]
  },

  { path: 'assessments/:workflowId/:documentId', component: AssessmentComponent, canActivate: [redirectUnauthorized, canViewItemGuard, canViewBusiness] },
  { path: 'jobs/:workflowId/:documentId', component: JobComponent, canActivate: [redirectUnauthorized, canViewItemGuard, canViewBusiness] },

  { path: 'estimates/:workflowId/:estimateId', component: EstimateComponent, canActivate: [redirectUnauthorized, canViewItemGuard, canViewBusiness] },
  { path: 'invoices/:workflowId/:invoiceId', component: InvoiceComponent, canActivate: [redirectUnauthorized, canViewItemGuard, canViewBusiness] },
  { path: 'payments/:workflowId/:paymentId', component: PaymentComponent, canActivate: [redirectUnauthorized, canViewItemGuard, canViewBusiness] },

  { path: 'items', component: ItemsComponent, canActivate: [redirectUnauthorized, canViewBusiness, canOpenItemsPage] },

  { path: 'suspended', component: SuspendedBusinessComponent, canActivate: [redirectUnauthorized, canViewSuspendedPage] },

  { path: 'loading', component: LoadingComponent },

  { path: '**', redirectTo: 'lobby', pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
